import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileLines } from '@fortawesome/pro-light-svg-icons/faFileLines';
import { faShieldCheck } from '@fortawesome/pro-light-svg-icons/faShieldCheck';
import { faKey } from '@fortawesome/pro-light-svg-icons/faKey';
import { faTrashCan } from '@fortawesome/pro-light-svg-icons/faTrashCan';
import { TypedMemo } from '../../common/types';
import { config } from '../../../config/config';
import { helpArticleUrl } from '../../support/helpCenter/helpCenterUrl';
import { LegalLinkRow } from './legal/LegalLinkRow';
import { CookieSettingsCard } from './legal/CookieSettingsCard';
import { legalMessages } from './legal/legalMessages';
import { SMLSettingsTabId } from './types';
import { Panel, PanelHead } from '../../music24/components/primitives/Panel';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Settings tabs; each part uses its own slice of it */
import cards from './settingsCards.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The legal pages of music24-website (pages/*.tsx). */
const DOCUMENTS = [
  { path: 'privacy-policy', title: legalMessages.privacyPolicy },
  { path: 'terms-of-service', title: legalMessages.termsOfService },
  { path: 'refund-policy', title: legalMessages.refundPolicy },
  { path: 'imprint', title: legalMessages.imprint },
];

/** Settings → Legal (design settings-redesign e1). */
const LegalTabComponent: React.FC = () => {
  const intl = useIntl();
  const [, setSearchParams] = useSearchParams();
  const websiteHost = config.smartLinksWebURL.replace(/^https?:\/\//, '').replace(/\/$/, '');
  const openAccount = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', SMLSettingsTabId.account);
      return next;
    });
  }, [setSearchParams]);

  return (
    <div className={cards.pane}>
      <Panel as="section">
        <PanelHead title={intl.formatMessage(legalMessages.documents)} />
        {DOCUMENTS.map((document) => (
          <LegalLinkRow
            key={document.path}
            icon={faFileLines}
            title={intl.formatMessage(document.title)}
            subtitle={`${websiteHost}/${document.path}`}
            mono
            href={`${config.smartLinksWebURL}${document.path}`}
          />
        ))}
      </Panel>
      <CookieSettingsCard />
      <Panel as="section">
        <PanelHead title={intl.formatMessage(legalMessages.yourData)} />
        <LegalLinkRow
          icon={faShieldCheck}
          title={intl.formatMessage(legalMessages.whatWeStore)}
          subtitle={intl.formatMessage(legalMessages.whatWeStoreDescription)}
          href={helpArticleUrl('account-and-privacy', 'data-we-store')}
        />
        <LegalLinkRow
          icon={faKey}
          title={intl.formatMessage(legalMessages.gdpr)}
          subtitle={intl.formatMessage(legalMessages.gdprDescription)}
          href={helpArticleUrl('account-and-privacy', 'your-rights-gdpr')}
        />
        <button type="button" className={`${cards.row} ${cards.rowButton}`} onClick={openAccount}>
          <span className={`${cards.rowIcon} ${cards.rowIconDanger}`}>
            <FontAwesomeIcon icon={faTrashCan} />
          </span>
          <span className={cards.rowText}>
            <span className={cards.rowTitle}>{intl.formatMessage(legalMessages.deleteTitle)}</span>
            <span className={cards.rowSub}>{intl.formatMessage(legalMessages.deleteDescription)}</span>
          </span>
          <span className={cards.rowEnd}>
            <span className={cards.link}>{intl.formatMessage(legalMessages.openAccount)}</span>
          </span>
        </button>
      </Panel>
    </div>
  );
};

export const LegalTab = TypedMemo(LegalTabComponent);
